import {
  Category,
  ProductType,
} from '../../../../data-access/products/products.models';

export type FilterOptions = {
  names: string[];
  categoryIds: string[];
  pricebookTypes: ProductType[];
  businessUnitIds: string[];
};

export function getFilteredCategories(
  availableCategories: Category[],
  filter: FilterOptions
): Category[] {
  const businessUnitIdsFilter = filter.businessUnitIds;
  const categories = businessUnitIdsFilter.length
    ? availableCategories.filter(
        (c) =>
          !c.businessUnitIds.length ||
          businessUnitIdsFilter.every((id) => c.businessUnitIds.includes(id))
      )
    : availableCategories;

  if (!filter.pricebookTypes.length) {
    return getCurrentCategoryLayer(
      categories,
      filter.names.join(' '),
      filter.categoryIds
    );
  }

  const categoriesFilteredbyType = filterCategoriesByType(
    categories,
    filter.pricebookTypes
  );

  return getCurrentCategoryLayer(
    categoriesFilteredbyType,
    filter.names.join(' '),
    filter.categoryIds
  );
}

function getCurrentCategoryLayer(
  availableCategories: Category[],
  searchTerm: string,
  selectedCategoryIds: string[]
): Category[] {
  if (searchTerm) {
    if (selectedCategoryIds.length > 0) {
      return getDescendantCategoriesBySearch(
        availableCategories,
        selectedCategoryIds,
        searchTerm
      );
    }
    return getCategoriesBySearchTerm(availableCategories, searchTerm);
  }

  if (selectedCategoryIds.length > 0) {
    return getDirectChildrenCategories(
      availableCategories,
      selectedCategoryIds
    );
  }
  return getTopLevelCategories(availableCategories);
}

function getTopLevelCategories(categories: Category[]): Category[] {
  return categories.filter((cat) => !cat.parentCategoryId);
}

function getDirectChildrenCategories(
  categories: Category[],
  selectedCategoryIds: string[]
): Category[] {
  return categories.filter(
    (cat) =>
      cat.parentCategoryId && selectedCategoryIds.includes(cat.parentCategoryId)
  );
}

function getCategoriesBySearchTerm(
  categories: Category[],
  searchTerm: string
): Category[] {
  return categories.filter((cat) =>
    cat.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
}

function getDescendantCategories(
  categories: Category[],
  parentIds: string[]
): Category[] {
  const result: Category[] = [];
  const queue = [...parentIds];

  while (queue.length) {
    const currentParentId = queue.shift();
    const children = categories.filter(
      (cat) => cat.parentCategoryId === currentParentId
    );
    if (children.length) {
      result.push(...children);
      children.forEach((child) => queue.push(child.categoryId));
    }
  }
  return result;
}

function getDescendantCategoriesBySearch(
  categories: Category[],
  selectedCategoryIds: string[],
  searchTerm: string
): Category[] {
  const descendants = getDescendantCategories(categories, selectedCategoryIds);
  return getCategoriesBySearchTerm(descendants, searchTerm);
}

export function filterCategoriesByType(
  categories: Category[],
  types: ProductType[]
): Category[] {
  const normalizedTypes = new Set(
    types.map((type) => (type === 'MATERIAL' ? 'Materials' : 'Services'))
  );

  return categories.filter((cat) => normalizedTypes.has(cat.categoryType));
}
